import type { ScopeWriteRefused, Store, SyncQueue, SyncState } from "../data/store.js";
import { LIST_KINDS, type Item, type List, type ListKind } from "../lib/api.js";
import type { User } from "../lib/auth.js";
import { el, setText } from "../lib/dom.js";
import { uncheckedPhrase } from "../lib/counts.js";
import { displayQuantity, formatDue, isOverdue, todayIso } from "../lib/format.js";
import { ApiError } from "../lib/http.js";
import { icon, type IconName } from "../lib/icons.js";
import type { Route } from "../lib/router.js";
import { currentTheme, onTheme, toggleTheme } from "../lib/theme.js";
import { navigationHoldsBlur } from "../lib/router.js";

/** Pieces shared by more than one screen. */

/** A route that names nothing: a wrong address, or an item this account cannot see. */
export function notFound(): Node[] {
  return [
    el("h1", { text: "Not found" }),
    el("a", { text: "← Lists", attrs: { href: "/", "data-link": true } }),
  ];
}

/** A labelled creation action below the rows it adds to. */
export function addRow(label: string, activate: (button: HTMLButtonElement) => void): HTMLButtonElement {
  const button = el("button", {
    class: "add-row",
    attrs: { type: "button" },
    children: [icon("plus"), el("span", { text: label })],
  });
  button.addEventListener("click", () => activate(button));
  return button;
}

/** A visible label bound to its control, shared by Add and Details. */
export function field(id: string, label: string, control: HTMLElement, error?: HTMLElement): HTMLElement {
  control.id = id;
  return el("div", {
    class: "field",
    children: [el("label", { class: "field-label", text: label, attrs: { for: id } }), control, error],
  });
}

/**
 * A screen, and the way to take it down again.
 *
 * Screens subscribe to the store now, and a subscription outlives the DOM it
 * was made for unless something cancels it: walking in and out of five lists
 * would otherwise leave five watchers behind, each one patching nodes that are
 * no longer on the page.
 */
export interface Screen {
  node: HTMLElement;
  /**
   * A route that names the same screen, arriving without a remount.
   *
   * Selecting a row is a navigation — it is in the URL so the back gesture can
   * close the sheet — and rebuilding the list to open a panel beside it would
   * throw away the scroll position and every row's place. A screen that has
   * nothing selectable on it leaves this out.
   */
  update?(route: Route): void;
  /**
   * The screen's one primary action right now, or none.
   *
   * A method rather than a field, because the answer changes: a list screen
   * has no heading to put the plus beside until the list arrives, and none
   * again once the list is deleted. The shell reads it at mount and again
   * whenever the screen calls `shell.refreshAction()`.
   */
  action?(): ScreenAction | undefined;
  /**
   * True while something typed on this screen would be lost by a reload: an
   * inline draft in a row, or the list's name mid-rename. A screen with
   * nothing to type on leaves this out. The shell's own surfaces -- Details,
   * the popup -- answer for themselves through `Chrome.dirty()`; together the
   * two are what holds a service-worker update back (Q04).
   */
  dirty?(): boolean;
  /** A submitted local write must finish before this screen can be left. */
  busy?(): boolean;
  /**
   * Arrived back from an item's note page (g77): the focus goes to that
   * item's row, on the menu button whose View note opened the page.
   */
  returnTo?(itemId: string): void;
  dispose(): void;
}

/**
 * What a screen offers the shell's plus.
 *
 * One plus per screen (I01, S04), and the shell owns the node: beside the
 * heading from 768 up, floating above the bottom navigation below that, and
 * never both. The screen says what it is called, where it sits when it sits
 * in the heading, and what pressing it does.
 */
export interface ScreenAction {
  /** The accessible name and the pointer tooltip: "Add item". */
  label: string;
  /** Where the plus goes at 768 and up. Below that it floats and this is unused. */
  slot: HTMLElement;
  activate(): void;
}

/**
 * Where a screen puts the panel for the row that is selected.
 *
 * The shell owns it because the container changes with the width and the
 * screen does not care which one it got: a bottom sheet under 768, a modal
 * where the list and the panel cannot sit side by side, and a column of the
 * layout where they can (g55). One panel node, moved between
 * them — see `src/views/shell.ts`.
 */
export interface DetailHost {
  /**
   * True when the panel is a column of the page rather than something opened
   * over it. The caller uses it to decide whether selecting a row is worth a
   * history entry: on a sheet it is, because back is how a sheet is closed.
   */
  persistent(): boolean;
  present(
    panel: HTMLElement,
    options: {
      /** The accessible name of the surface it lands in. */
      label: string;
      /** Visible heading when it differs from the accessible name. */
      title?: string;
      /** The person closed it — Escape, the backdrop, or a button in it. */
      onDismiss(): void;
      /**
       * Where the focus goes when the panel had it and is taken down. A
       * function is read at that moment: the row that opened the panel may
       * be gone by then — deleted from it — and the screen knows what is
       * left where it was.
       */
      returnFocus?: HTMLElement | (() => HTMLElement | undefined);
      /**
       * The dirty-draft guard (E05), asked on every way out the host owns —
       * Close, Escape, the backdrop — and by `dismiss()` and `guarded()`.
       * While `dirty()` is true the surface shows the question instead;
       * `keep` puts the panel back untouched, `discard` goes on.
       */
      guard?: DraftGuard;
    },
  ): void;
  /** True while the panel's guard says it holds changes. False with no guard, or no panel. */
  dirty(): boolean;
  /**
   * True while a question is up over the panel — the host's own discard one,
   * or a caller's through `confirm()`. Browser Back reads it: an entry that
   * was spent on a panel with a question over it is put back, so that Back
   * answers the question's safe choice rather than closing the panel.
   */
  asking(): boolean;
  /**
   * Runs `proceed` now when nothing changed, and otherwise asks first: Keep
   * editing puts the panel back, Discard runs it. For an action that is not
   * dismissal — the screen selecting another row over a draft.
   */
  guarded(proceed: () => void): void;
  /** The guarded way out: `guarded(onDismiss)`. A panel's own Cancel calls this. */
  dismiss(): void;
  /**
   * A confirmation as a state of this surface, never a second one over it:
   * the title swaps, the panel stays mounted but hidden, and the question
   * sits in its place. g08 opens "Delete 'Buy milk'?" through this.
   */
  confirm(spec: ConfirmSpec): void;
  /** Takes the question down, and puts the title, the panel and the focus back. */
  resume(): void;
  /** Takes the panel down without calling `onDismiss` — the caller knows. */
  clear(): void;
}

/**
 * What a surface asks before throwing away a draft. Shared by the popup host
 * and the detail host, so Add item and Details cannot come to ask differently.
 */
export interface DraftGuard {
  dirty(): boolean;
  title: string;
  message?: string;
  keep: string;
  discard: string;
}

/**
 * What a screen needs from whatever frame it is drawn in: the store and the
 * surfaces the frame owns. The account's frame is a `Shell`; a shared list's
 * page (g72) is drawn in a frame with no account at all, so a screen that
 * can be drawn there asks for this and nothing more.
 */
export interface ScreenHost {
  store: Store;
  detail: DetailHost;
  /**
   * Says something once, politely. The shell owns the region because there is
   * one of it and there are many screens — and because a region has to be in
   * the accessibility tree before its text changes, which a per-screen slot
   * that is hidden until it has content never is.
   */
  announce(message: string): void;
  /** Opens a form over the screen, in the shell's popup host. See `PopupSpec`. */
  openPopup(spec: PopupSpec): Popup;
  /**
   * A question over the screen, standing alone in the popup host: one
   * history entry, Cancel focused, and Escape, Back and the backdrop are
   * all the safe answer. For a Delete chosen from a menu, where there is no
   * panel for the question to be a state of (D08).
   */
  confirm(spec: ConfirmSpec, options?: ConfirmOptions): Asked;
  /** Tells the shell the answer to `Screen.action()` has changed. */
  refreshAction(): void;
  /**
   * True below the 768px rail breakpoint, where a phone reads its rows
   * without a drag column and reorders in an explicit mode (g53). The shell
   * owns every `matchMedia`, so a screen asks here rather than measuring.
   */
  narrow(): boolean;
  /** Called on every crossing of that breakpoint. Returns the unsubscribe. */
  onNarrow(listener: (narrow: boolean) => void): () => void;
}

/**
 * What a screen needs from the account's shell: a `ScreenHost`, and the two
 * things a screen cannot do for itself because they end the session or
 * replace the screen.
 */
export interface Shell extends ScreenHost {
  user: User;
  /**
   * Unconditional: ends the session and wipes this device. Settings asks
   * first when something is unsent (g60), in `signOutControl`.
   */
  signOut(): void;
  reauth(): void;
  /** Takes the account as the server now has it: its name, after a change (g73). */
  updateUser(user: User): void;
  /**
   * While a screen reads a store other than the account's, such as an added
   * shared list's (g137), the frame's Category suggestions come from it and
   * its sync notices show. Returns the way to hand the frame back.
   */
  lend?(store: Store): () => void;
}

/**
 * A surface opened over the screen: a centred dialog from 768 up and a bottom
 * sheet below, the same one Details uses (U03), and modal at every width.
 *
 * The body is the whole form, actions included. A host-owned footer looked
 * tidier and would have split the form from its submit button, which is the
 * one thing a footer has to be inside the form to be.
 */
export interface PopupSpec {
  /** Navigation can protect a copyable draft even when local dismissal is allowed. */
  navigationDirty?(): boolean;
  /** A submitted local write cannot be cancelled by dismissing its form. */
  busy?(): boolean;
  title: string;
  body: HTMLElement;
  /** Focused on open. Defaults to the first control in the body. */
  focus?: HTMLElement;
  /**
   * Focused on close. Falls back to the screen's plus, then to the screen's
   * heading — the trigger may be a row that is gone by then. A function is
   * read at that moment, for a caller whose trigger goes *because* of what
   * the popup did: a Delete confirmed here removes the row that opened it,
   * and the screen knows what is left where it was.
   */
  returnFocusTo?: HTMLElement | (() => HTMLElement | undefined);
  /**
   * The dirty-draft guard, asked on every way out the person takes — Cancel,
   * Close, Escape, the backdrop and browser Back — and never on `close()`.
   * While `dirty()` is true the surface swaps to a confirmation; `keep` puts
   * the form back untouched, `discard` closes.
   */
  guard?: DraftGuard;
  /** After the surface has gone, by any path. Where a form lets go of its subscriptions. */
  onClose?(): void;
}

export interface Popup {
  isOpen(): boolean;
  /** The guarded way out. A form's own Cancel calls this. */
  dismiss(): void;
  /** The unguarded way out: after a successful write, or from Discard. */
  close(): void;
  /** Swaps the body for a confirmation. The body's values survive off-screen. */
  confirm(spec: ConfirmSpec): void;
  /** Puts the body back, and the focus where the spec asked for it. */
  resume(): void;
  /**
   * Swaps the body for another step of the same task, as `confirm` does for a
   * question (g72: the name a shared list needs before the Add can go
   * through). Escape, Close, the backdrop and Back all answer `cancel`, and
   * the body's values survive off-screen until `resume`.
   */
  step(spec: PopupStep): void;
}

export interface PopupStep {
  title: string;
  body: HTMLElement;
  /** Focused when the step shows. Defaults to its first control. */
  focus?: HTMLElement;
  /** The safe answer: every way out of the step but its own button. */
  cancel(): void;
}

/**
 * How long a red button ignores the finger after the question appears.
 *
 * The question replaces the whole surface, so the button lands where the
 * menu entry that opened it may have been — on a phone sheet that shrinks to
 * fit, exactly there. The tap that opened it, or the second half of a double
 * tap, must not be the tap that deletes (D08). A keyboard never notices: the
 * focus starts on the safe choice, and reaching the red one takes longer.
 */
export const CONFIRM_INERT_MS = 350;

/**
 * A confirmation: a question, the safe answer first and focused, and the
 * consequential one beside it. Host-free, so the popup can swap to it and the
 * Delete confirmations put it in whichever surface they land in.
 */
export interface ConfirmSpec {
  title: string;
  message?: string;
  /** The safe choice. First in the DOM and focused first. */
  keep: { label: string; onChoose(): void };
  /**
   * The consequential one. `danger` fills it red, for an act that destroys
   * what can't be recovered: a Delete, a Disconnect, a recovered import's
   * Discard, unsent changes at Sign out (g60). Never a draft's Discard.
   */
  act: { label: string; onChoose(): void; danger?: boolean };
}

/**
 * What the Delete question says (D08), and the sentence it adds when the
 * row's panel holds unsaved edits: deleting discards them, and saying so here
 * is what makes a second question about them afterwards redundant (D09).
 * Here rather than in the editor, which no longer deletes anything: the
 * question is asked from the row's actions menu, on either screen.
 */
export const DELETE_ITEM = {
  message: "This item will be removed from your list. You can't undo this in Trayflux.",
  discards: "Your unsaved edits will be discarded too.",
};

/** The question's title, naming the item as it was last saved. */
export const deleteTitle = (text: string): string => `Delete '${text}'?`;

/** A standalone question, from `ScreenShell.confirm`: the screen's way to close it early. */
export interface Asked {
  /** The unguarded way out, as `Popup.close`; the safe answer runs, the act does not. */
  close(): void;
}

export interface ConfirmOptions {
  /** As `PopupSpec.returnFocusTo`: read at close, after the act has run. */
  returnFocusTo?: HTMLElement | (() => HTMLElement | undefined);
}

/**
 * A form's or a dialog's row of actions (g75): the ways out and the lesser
 * actions first, then the one main action, last and rightmost. The DOM, the
 * Tab order and the eye all read dismissal before the main action, in a row
 * or stacked, and the signature makes that the only order a caller can ask
 * for. `main` is left out where a state has nothing to do but leave.
 */
export function actionRow(
  secondary: readonly (HTMLElement | false | undefined)[],
  main?: HTMLElement,
  extra?: string,
): HTMLDivElement {
  const row = el("div", { class: extra ? `popup-actions ${extra}` : "popup-actions", children: [...secondary, main] });
  stackWhenCrowded(row);
  return row;
}

/**
 * Stacks a row whose buttons would not fit on one line, whatever the reason:
 * a narrow sheet, long words or enlarged text. A width threshold cannot do
 * it, since Add's three buttons fit at 375 where the name prompt's three do
 * not (g75). The row is laid out as one line, and if any shown button lands
 * below the first, `data-stacked` puts each on a line of its own, full width,
 * in the same order. The observer watches the row and every button, so a new
 * width, text size or label, or a button shown or hidden, asks again; the
 * answer is written back in the same callback, so an unchanged answer
 * changes no size and asks nothing more. jsdom has no ResizeObserver, and
 * there the row simply stays a row.
 */
function stackWhenCrowded(row: HTMLElement): void {
  if (typeof ResizeObserver !== "function") return;
  const settle = (): void => {
    delete row.dataset["stacked"];
    const tops = [...row.children]
      .filter((child) => child.getClientRects().length > 0)
      .map((child) => child.getBoundingClientRect().top);
    if (tops.some((top) => Math.abs(top - tops[0]!) > 1)) row.dataset["stacked"] = "";
  };
  const observer = new ResizeObserver(settle);
  observer.observe(row);
  for (const child of row.children) observer.observe(child);
}

export function confirmPanel(spec: ConfirmSpec): { node: HTMLElement; focus: HTMLButtonElement } {
  const keep = el("button", {
    class: "secondary",
    text: spec.keep.label,
    attrs: { type: "button" },
    on: { click: spec.keep.onChoose },
  });
  const born = performance.now();
  const inert = () => Boolean(spec.act.danger) && performance.now() - born < CONFIRM_INERT_MS;
  const act = el("button", {
    class: spec.act.danger ? "danger" : undefined,
    text: spec.act.label,
    attrs: { type: "button" },
    on: {
      // A press in the window moves nothing, not even the focus: a button
      // takes the focus on mousedown whatever its click does, and a tap that
      // parked the focus on Delete would make the next Enter the delete.
      mousedown: (event) => {
        if (inert()) event.preventDefault();
      },
      click: () => {
        if (inert()) return;
        // Once. What a Delete does cannot be done twice, and a double tap
        // that lands both halves here would ask it to. A disabled button
        // receives no click, from a finger or from `.click()`.
        act.disabled = true;
        spec.act.onChoose();
      },
    },
  });
  // Said on the button while it lasts, for a browser driver to wait on
  // rather than the clock (g54). Cleared no sooner than the window ends.
  if (spec.act.danger) {
    act.dataset["inert"] = "";
    setTimeout(() => delete act.dataset["inert"], CONFIRM_INERT_MS);
  }
  const node = el("div", {
    class: "confirm",
    children: [
      spec.message === undefined ? undefined : el("p", { class: "confirm-message", text: spec.message }),
      actionRow([keep], act),
    ],
  });
  return { node, focus: keep };
}

/**
 * What the sync state is allowed to say, in one place, for the three surfaces
 * that say it: the rail foot, the Settings page, and the mark on the Settings
 * tab.
 *
 * Two facts rather than one: `queue` is what the person is actually asking —
 * is my typing safe — and `link` is why it has not gone yet. A flat list of
 * states would force a choice between them, and the offline-with-changes case
 * is exactly where saying only "Saved on this device" drops the more useful
 * half of what this device knows.
 *
 * The design system is binding on the vocabulary: "'Saved on this device' and
 * 'Synced' are different states" (DESIGN-SYSTEM.md:26), and "Synced" may never
 * be claimed from a local write (:141). It is said here only when the queue is
 * empty, which can only happen after a pass the server answered. And "Saved on
 * this device" is not claimed before IndexedDB has the write: until then the
 * queue is `writing` and the line says "Saving…" (g67).
 */
export interface SyncWords {
  glyph: IconName;
  /** The queue fact. Always said. */
  headline: string;
  /** The last attempt, said only when it was not clean. */
  detail?: string;
}

export function syncWords(state: SyncState): SyncWords {
  const wrong =
    state.queue === "unsaved" || state.link === "failed" || state.link === "signed-out"
    || state.link === "held" || state.link === "ended";
  const unsent = state.queue === "writing" || state.queue === "pending";
  const glyph: IconName = wrong ? "alert" : unsent ? "cloudPending" : "cloud";

  // Before every link branch, because it outranks all of them: if the change
  // never reached this device, why it has not reached the server is not the
  // thing to say. It is also the one state here the person can act on by
  // retyping, which they will not do if told their work is saved.
  if (state.queue === "unsaved") {
    return {
      glyph,
      headline: "Not saved on this device",
      detail: "Your last change is only on screen. Try it again.",
    };
  }

  if (state.link === "signed-out") {
    return {
      glyph,
      headline: "Signed out on this device",
      // Only when there is something to lose. Saying "your changes are saved
      // here" over an empty queue invents a reassurance about nothing.
      detail: state.queue === "pending" ? "Your changes are saved here." : undefined,
    };
  }

  // A shared list's link stopped working (g71). Never "Synced": nothing more
  // will be, and a change queued before the end may or may not have landed.
  if (state.link === "ended") {
    return unsent
      ? { glyph, headline: "Not sent", detail: "This link no longer works. Changes made here may not have been saved." }
      : { glyph, headline: "Not syncing", detail: "This link no longer works." };
  }

  // A shared list waiting on the person: a name, a sign-in, or someone else
  // now using this browser (g71). The page says which.
  if (state.link === "held") {
    return unsent
      ? { glyph, headline: queueHeadline(state.queue), detail: "Not sent until you continue." }
      : { glyph, headline: "Not syncing" };
  }

  const headline = queueHeadline(state.queue);

  if (state.link === "failed") return { glyph, headline, detail: "Sync failed." };
  if (state.link === "unreachable") {
    return {
      glyph,
      headline,
      detail:
        unsent
          ? "Can't reach Trayflux. Your changes will sync when you reconnect."
          : "Can't reach Trayflux.",
    };
  }
  return { glyph, headline };
}

/**
 * The mark on the Settings tab, which is the only sync signal a phone shows
 * without opening anything.
 *
 * Nothing when the queue is empty: a mark that is always there is a mark nobody
 * reads. It appears when something of *yours* is unsent, which is the only case
 * where the person might want to do something about it — and it changes shape
 * rather than only colour, because a coloured dot alone fails WCAG 1.4.1 and
 * this codebase has already shipped that mistake once, on overdue rows.
 */
/**
 * Exhaustive on purpose: a queue state this forgot fell through to "Synced"
 * once already, which is the one word the line must never say over unsent work.
 */
function queueHeadline(queue: Exclude<SyncQueue, "unsaved">): string {
  switch (queue) {
    case "writing":
      return "Saving…";
    case "pending":
      return "Saved on this device";
    case "empty":
      return "Synced";
    default: {
      const never: never = queue;
      return never;
    }
  }
}

export function syncMark(state: SyncState): IconName | undefined {
  // Before the `pending` gate, not inside it: a write that never reached the
  // mirror is not pending, and it is the one state here most worth a mark.
  if (state.queue === "unsaved") return "alert";
  if (state.queue === "empty") return undefined;
  return state.link === "failed" || state.link === "signed-out" || state.link === "held" || state.link === "ended"
    ? "alert" : "cloudPending";
}

/**
 * The persistent line: compact in the rail foot, whole on the Settings page.
 *
 * Deliberately not a live region. It changes on every drain, and a screen
 * reader announcing "Saved on this device" then "Synced" each time somebody
 * ticks a box is noise standing between them and the list. The banner below is
 * the surface that interrupts, and it only exists when there is something to do.
 *
 * The rail's is one row beside the Settings gear (g48): the glyph and the
 * headline, with the detail kept for screen readers only, since the glyph's
 * shape is what tells a failed queue from a pending one and it is
 * `aria-hidden`. The page says everything.
 */
export function syncLine(store: Store, where: "rail" | "page"): Screen {
  const node = el("div", { class: `sync sync-${where}` });

  const paint = () => {
    const state = store.syncState();
    // "Synced" is never claimed before the first sync has landed: on a device
    // that has not reached the server it is the claim DESIGN-SYSTEM.md:141
    // rules out by name. Unsent work is another matter -- it is as true offline
    // as online, and a rail that hid it until the first pass would hide it
    // exactly when it matters, on a device that opened without a connection.
    let words: SyncWords | undefined;
    if (store.status() === "ready" || syncMark(state) !== undefined) words = syncWords(state);
    else if (where === "page") words = { glyph: "cloudPending", headline: "Not synced yet" };
    if (!words) {
      node.replaceChildren();
      return;
    }
    const detail = words.detail === undefined
      ? []
      : [el("span", { class: where === "rail" ? "visually-hidden" : "sync-detail", text: words.detail })];
    node.replaceChildren(
      icon(words.glyph),
      el("div", {
        class: "sync-words",
        children: [el("span", { class: "sync-headline", text: words.headline }), ...detail],
      }),
    );
  };

  const stopState = store.onSyncState(paint);
  const stopStatus = store.onStatus(paint);
  paint();

  return {
    node,
    dispose() {
      stopState();
      stopStatus();
    },
  };
}

/**
 * The two things every screen has to be able to say about syncing, in one slot.
 *
 * An expired session wins over a problem message, and deliberately: the 401
 * emits a problem of its own, and "Sign in again." beside "sign in again" is
 * one thing said twice.
 *
 * Nothing here mentions being offline. Reads come off the mirror and writes go
 * into a queue, so a phone in a lift is the ordinary case rather than a fault,
 * and a strip that announces it every time would be furniture the app does not
 * need. A screen with *nothing to show* says so itself — that is the one time
 * "can't reach the server" is the most useful thing on the page.
 *
 * The rule this slot keeps, now that `syncLine` states the state everywhere:
 * **a banner exists only when there is something for the person to do.** If one
 * is on screen, there is a button on it. Being offline is not on that list; a
 * dead session and a queue the server has stopped accepting are.
 */
export function syncNotices(
  store: Store,
  onReauth: () => void,
  say: (message: string) => void,
): Screen {
  // `status` for the `:empty` rule that keeps an empty slot from taking up
  // space, `notices` because a screen has two of these and they are not the
  // same slot: this one speaks about syncing, the other about the screen.
  //
  // The slot is not itself a live region — it is hidden while empty, which puts
  // it outside the accessibility tree exactly when a region would need to be
  // in it. The shell's `announce` is where this is said; see `Chrome`.
  const node = el("div", { class: "status notices" });
  let problem: string | undefined;
  /**
   * What was last announced, so a repaint is not a repetition.
   *
   * `paint` runs on every sync-state change, which is every drain. Announcing
   * from inside it unguarded would say "Signed out on this device" into a
   * screen reader every few seconds for as long as the session stayed expired.
   */
  let said: string | undefined;

  const show = (message: string | undefined, children: HTMLElement[]) => {
    node.replaceChildren(...children);
    if (message !== said) {
      said = message;
      if (message !== undefined) say(message);
    }
  };

  const paint = () => {
    if (store.paused()) {
      const message =
        "Signed out on this device. Your changes are saved and will sync once you are back in.";
      show(message, [notice(message, { label: "Sign in again", onClick: onReauth })]);
      return;
    }

    // The engine has given up retrying, so the only way on is a person asking
    // again. Dismiss would be the wrong button here: it offers to stop
    // mentioning the problem instead of offering to fix it.
    if (store.syncState().link === "failed") {
      const message = problem ?? "Your changes are still on this device.";
      show(message, [notice(message, { label: "Try again", onClick: () => void store.sync() })]);
      return;
    }

    show(
      problem,
      problem === undefined
        ? []
        : [
            notice(problem, {
              label: "Dismiss",
              onClick: () => {
                problem = undefined;
                paint();
              },
            }),
          ],
    );
  };

  // One per drain, not one per rejection — the engine already collapses them,
  // and the newest is the one worth keeping.
  const stopProblems = store.onProblem((message) => {
    problem = message;
    paint();
  });
  // `onSyncState` rather than `onPaused`: this slot now turns on two of the
  // link's values, and watching only one of them leaves the other repainting
  // by luck — whenever a problem message happens to arrive alongside it.
  const stopState = store.onSyncState(paint);

  paint();

  return {
    node,
    dispose() {
      stopProblems();
      stopState();
    },
  };
}

function notice(message: string, action: { label: string; onClick: () => void }): HTMLElement {
  return el("p", {
    class: "banner",
    children: [
      el("span", { text: message }),
      el("button", {
        class: "banner-action",
        text: action.label,
        attrs: { type: "button" },
        on: { click: action.onClick },
      }),
    ],
  });
}

/**
 * The theme switch, on the Settings page (g48).
 *
 * It follows `onTheme` rather than owning the truth, and the subscription
 * comes back with the node: the page is disposed on every departure, and a
 * button left subscribed is a detached button repainted on every theme change
 * for the rest of the session.
 *
 * g21 supersedes the pinned reference's fixed-label toggle: the visible name
 * and decorative glyph describe the destination theme. Keep the button itself
 * so updating either control never loses keyboard focus.
 */
export function themeToggle(): { node: HTMLButtonElement; dispose(): void } {
  const node = el("button", {
    class: "secondary theme-toggle",
    attrs: { type: "button" },
  });
  const update = (theme: ReturnType<typeof currentTheme>) => {
    node.replaceChildren(
      icon(theme === "dark" ? "sun" : "moon"),
      // A verb (g56): "Dark theme" alone read as the state it is not in.
      el("span", { text: theme === "dark" ? "Use light theme" : "Use dark theme" }),
    );
  };
  update(currentTheme());
  node.addEventListener("click", () => void toggleTheme());
  const stop = onTheme(update);

  return { node, dispose: stop };
}

export function logo(): HTMLElement {
  // The 192px icon, not the 1254px master: a 900 KB download for a 72px mark
  // would be absurd, and the master is deliberately not precached.
  return el("img", {
    class: "logo",
    attrs: { src: "/icons/pwa-192x192.png", width: 88, height: 88, alt: "Trayflux" },
  });
}

/**
 * A shared list's refusals (g71), in words (g72). The page's controls should
 * never offer these, so each is what a person sees only when something moved
 * under them: the link ended, or a change was already on its way.
 */
export const REFUSED: Record<ScopeWriteRefused["reason"], string> = {
  list_op: "Only the list's owner can change the list itself.",
  other_list: "That change belongs to another list.",
  view: "This link can only view the list.",
  name: "Add your name before making changes.",
  held: "Changes to this list are on hold for now.",
  ended: "This link no longer works, so changes can't be saved.",
  not_writable: "This link can't make changes right now.",
};

/**
 * By name rather than `instanceof`: this file reads the store only for its
 * types, and the store is a module some screens' tests replace whole.
 */
function refusedByScope(error: unknown): error is ScopeWriteRefused {
  return error instanceof Error && error.name === "ScopeWriteRefused" &&
    Object.hasOwn(REFUSED, (error as { reason?: unknown }).reason as string);
}

/**
 * What to actually show a person.
 *
 * A validation failure carries `fields` precisely so a caller need not guess,
 * and "The request body is not valid." helps nobody standing in a supermarket.
 */
export function messageOf(error: unknown): string {
  if (refusedByScope(error)) return REFUSED[error.reason];
  if (!(error instanceof ApiError)) return "Something went wrong.";

  const field = error.fields[0];
  if (field) return field.path ? `${field.path}: ${field.message}` : field.message;

  return error.message;
}

export function banner(message: string): HTMLElement {
  return el("p", { class: "banner", text: message });
}

/**
 * The one time being offline is worth a word.
 *
 * Reads come off the mirror and writes go into a queue, so a phone in a lift is
 * the ordinary case rather than a fault — except on a screen that has nothing
 * at all to show, where an empty page would be a guess dressed up as a fact.
 *
 * Shared because it was written out three times, identically, and a sentence
 * copied three times is a sentence that will disagree with itself. Each screen
 * still words its *own* empty and loading states, which genuinely differ.
 */
export function unreachable(store: Store): HTMLElement {
  return el("div", {
    class: "empty",
    children: [
      el("p", { class: "muted", text: "Can't reach Trayflux, and nothing is saved here yet." }),
      el("button", {
        class: "secondary",
        text: "Try again",
        attrs: { type: "button" },
        on: { click: () => void store.sync() },
      }),
    ],
  });
}

/**
 * The row's text, as a control rather than a label.
 *
 * `design/DESIGN-SYSTEM.md:105` and `:157`: clicking the text starts an edit in
 * place. It never opens the inspector and never changes the checked state, and
 * clicking an empty part of the row does nothing at all — which is why the row
 * body stopped being one big button.
 *
 * Shared between the list and the projections, unlike the row itself, because
 * the rules here are fiddly and unobvious — what Escape does, what an empty
 * value does, what an arriving delta is allowed to overwrite — and two copies
 * of fiddly rules are two sets of rules within a release or two.
 */
export interface InlineText {
  /** Stable. The row inserts it once; the swap happens inside. */
  node: HTMLElement;
  /** True while an editor is open, which is when a repaint must keep its hands off. */
  editing(): boolean;
  /** True until a submitted asynchronous write settles. */
  pending(): boolean;
  /**
   * True while an editor is open *and* has been typed into. An open editor
   * nobody has touched holds nothing worth keeping a row on screen for (C04):
   * `finish` drops it, the way an explicit switch does.
   */
  dirty(): boolean;
  /**
   * What the store says the text is, and what leads it on the button.
   *
   * A `prefix` is drawn before the text as its own span with one space
   * between (g58): a Shopping row's quantity. It is never part of the text,
   * so the field an editor opens holds only the text, and it leaves with the
   * button while an editor is open. Without one the button holds the text
   * alone, which is all the heading ever asks for. Written to the button
   * while an editor is open too, which the open draft never sees.
   */
  show(text: string, prefix?: string): void;
  /**
   * Opens the editor from outside, focused and with the text selected.
   *
   * Exists for a second way in. The button is the ordinary one; a list's
   * heading also offers Rename in its options surface, and that entry has to
   * reach the same editor rather than grow its own.
   */
  open(): void;
  /**
   * Ends an open edit from outside, the way an explicit action elsewhere should.
   *
   * A touched draft is committed and an untouched one is dropped. This exists
   * because the Details button and the panel behind it are a *second* editor for
   * the same item — without it the two can both be open, the panel can save, and
   * an Enter in the forgotten textarea then overwrites what the panel wrote.
   *
   * **Returns false when the write was refused**, and the editor is then still
   * open with the draft in it. A caller that was about to open something else
   * has to stop, or the refusal costs the person their text.
   */
  finish(): boolean;
  /**
   * The item under an open, typed-into editor was deleted on another device
   * (g54). The draft is not thrown away with it: the field stays, read-only
   * so its text can still be selected and copied, with `message` beside it
   * and said aloud. Save goes, since there is nothing left to save to;
   * Cancel and Escape call `onDiscard`, and Enter does nothing. The editor
   * leaves its group, so opening another is not held up by a draft that can
   * no longer be committed.
   */
  orphan(message: string, onDiscard: () => void): void;
}

/**
 * The one open editor on a screen, and what opening another does to it.
 *
 * This is E04's contract, held in one place: an explicit switch to another
 * editor — a second row's text, the heading's rename, the Details panel —
 * resolves the draft in hand first. A valid one is committed, an untouched
 * one dropped, a blank one refused with the switch stopped. Before this,
 * two rows could hold open editors at once and only Details resolved
 * anything, and the agenda kept its own loop over every row to do the same
 * job before a rebuild. One group per screen, handed to every `inlineText`
 * it builds, is what makes "shared" a fact rather than two copies.
 *
 * Not a blur rule, and it must not become one: the checkbox beside a field
 * takes focus too, and that gesture changes completion and nothing else.
 */
export interface EditorGroup {
  /** True while an editor in the group is open. */
  editing(): boolean;
  /**
   * Resolves the open draft, if any. False when the switch must not happen:
   * the editor is still open with its draft, and focus is back in it.
   */
  resolve(): boolean;
  /**
   * An editor in the group is about to open. The list screens end their
   * Reorder mode here (g53), before the field is built, so it is measured at
   * the width it will be typed in.
   */
  opening(): void;
  /** For `inlineText` only. */
  opened(editor: InlineText): void;
  closed(editor: InlineText): void;
}

export function editorGroup(hooks: { opening?(): void } = {}): EditorGroup {
  let open: InlineText | undefined;

  // A row deleted from under its editor never closes it: the node leaves the
  // document with the draft still in it, which is the behaviour before this
  // too. Forgotten here rather than resolved, so a dead editor cannot hold
  // the screen hostage — refusing every switch and every rebuild — over a
  // draft that has already gone.
  const live = (): InlineText | undefined => {
    if (open && !open.node.isConnected) open = undefined;
    return open;
  };

  return {
    editing: () => live() !== undefined,
    resolve: () => live()?.finish() ?? true,
    opening: () => hooks.opening?.(),
    opened(editor) {
      open = editor;
    },
    closed(editor) {
      if (open === editor) open = undefined;
    },
  };
}

/**
 * Whatever a textarea will accept, reduced to what a single-line field means.
 *
 * The API's `singleLine` refuses control characters outright, so a pasted
 * newline would be accepted here, queued, and then refused by the server —
 * isolated as a poison mutation and reverted, which reads as the edit quietly
 * undoing itself some minutes later. An `<input>` could not hold one; a
 * textarea can, so switching to one for the layout brought this with it.
 */
const CONTROL_RUN = /[\u0000-\u001f\u007f]+/g;

function oneLine(value: string): string {
  return value.replace(CONTROL_RUN, " ").trim();
}

/**
 * What a blank draft is told, beside the field and through the live region.
 *
 * One string, exported, so the test asserts the words the spec chose (E03)
 * rather than a paraphrase, and the two places it is said cannot drift.
 */
export const EMPTY_ITEM = "Enter an item name.";

/**
 * Beside a row's own editor when its item is deleted on another device
 * (g54): the row stays for the words typed into it, and says so.
 */
export const ROW_GONE = "Deleted on another device. Your edit is kept here to copy; Cancel removes it.";

/**
 * Once a row has become a kept draft (g54): a focus left on a control the
 * row withdrew -- its ellipsis, handed back by a menu that closed -- or
 * dropped to the page goes to the draft's field. A focus anywhere else,
 * Cancel included, stays where it is.
 */
export function keepFocusIn(row: HTMLElement): void {
  const at = document.activeElement;
  const field = row.querySelector<HTMLTextAreaElement>("textarea");
  if (!field || at === field) return;
  const stranded = at === null || at === document.body || !at.isConnected
    || (row.contains(at) && (at.closest("[hidden]") !== null || (at as HTMLInputElement).disabled === true));
  if (stranded) field.focus({ preventScroll: true });
}

/**
 * One id per open editor, for `aria-describedby`. A counter rather than the
 * item's id: the heading has no item, and an id has to be unique in the
 * document, not merely per row.
 */
let editorSerial = 0;

/** Whether the browser sizes a field to its content (`field-sizing`), which the stylesheet asks for where it can. */
const sizesItself = (): boolean =>
  typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("field-sizing", "content");

export function inlineText(options: {
  text: string;
  /**
   * The accessible name of the button, given the current text and the prefix
   * leading it, if any. The field an editor opens is named with the text
   * alone, since the text is all it edits.
   */
  label(text: string, prefix?: string): string;
  /** Returns false when the write was refused; the draft is then kept. */
  commit(next: string): boolean | Promise<boolean>;
  /**
   * Called when an editor opens (`true`) or closes (`false`).
   *
   * The row repaints from here rather than the class being toggled directly,
   * because `rowPresentation` assembles `className` whole — see the note on
   * `editing` there. Nothing else can tell that an editor opened: opening one
   * is not a change to the item, so no delta arrives and nothing repaints.
   *
   * The flag matters at the wide breakpoint, where the detail panel stays on
   * screen beside a row that is still live: opening an editor there has to
   * dismiss the panel, or the item has two editors pointed at it again — the
   * same fault as Details, from the other direction.
   */
  onToggle?(open: boolean): void;
  /**
   * What the field will hold, when it is not an item's 1000.
   *
   * A list name is 200 at the API, and a field that accepts more than the
   * server will take is a refusal waiting to happen at the end of the typing
   * rather than the start.
   */
  maxLength?: number;
  /** An element whose text describes the button, beyond naming it. */
  describedBy?: string;
  /**
   * Whether focus leaving the field commits a touched draft.
   *
   * Off for rows, and the long note further down says why. On for a list's
   * heading, where the behaviour predates this helper and H01 keeps it.
   */
  commitOnBlur?: boolean;
  /**
   * Whether Enter on an empty value closes the editor instead of refusing.
   *
   * Off for rows: an empty item is not a deletion, and a stray
   * select-all-and-backspace must not be able to destroy one. On for a list's
   * name, where H01 asks for the old name back and no mutation — which is
   * what closing does, since closing repaints from `text`.
   */
  emptyRestores?: boolean;
  /**
   * Whether Enter and Escape are the only way out — no Save and Cancel pair.
   *
   * Off for rows: E02 gives a phone visible controls for the edit in hand.
   * On for a list's heading, whose behaviour H01 already fixed as Enter,
   * Escape and blur — and with `commitOnBlur` a Save button could never be
   * reached, since the click that aimed at it would have blurred and saved
   * the field first.
   */
  keysOnly?: boolean;
  /**
   * Where a refusal is also said out loud. Rows pass the shell's live region.
   *
   * The message is already beside the field and linked to it, but a
   * description that changes under a focused field is not announced by
   * anything, so somebody who cannot see it would press Enter and hear
   * nothing happen.
   */
  announce?(message: string): void;
  /**
   * The screen's group of editors, of which one may be open at a time.
   *
   * Opening this one resolves whichever is open first, and does not open at
   * all if that refuses. Inside `open` rather than on the button's click, so
   * every way in — the button, `open()` from the options dialog, whatever
   * comes next — goes through it.
   */
  group?: EditorGroup;
  /**
   * A row that reads rather than edits (g72): while `active()`, pressing the
   * text runs `activate` -- a view link's Details -- and never opens a field.
   * Asked at every press and every repaint, since a link can end on screen.
   */
  readOnly?: {
    active(): boolean;
    label(text: string, prefix?: string): string;
    activate(): void;
  };
}): InlineText {
  let text = options.text;
  // What leads the text on the button, and the two spans that draw it: made
  // the first time there is one, and kept, so a repaint that changes neither
  // replaces no text node (see `paintButton`).
  let prefix: string | undefined;
  let prefixNode: HTMLSpanElement | undefined;
  let words: HTMLSpanElement | undefined;
  let input: HTMLTextAreaElement | undefined;
  // Re-fits the open field when its width changes: see `open`.
  let resized: ResizeObserver | undefined;
  /**
   * Whether the person has typed anything since the editor opened.
   *
   * Not a string comparison against `text`, because `show()` moves `text` when
   * a delta arrives: open "Milk", have another device rename it, press Enter
   * without touching the field, and a comparison would see "Milk" against the
   * new name, call it a change, and overwrite the newer value with the older.
   */
  let dirty = false;
  let pending = false;
  let retry = false;

  const button = el("button", {
    class: "item-text-button",
    attrs: {
      type: "button",
      "aria-label": options.label(text),
      // Set once, and it survives every open and close: the button node is
      // reused rather than rebuilt, which is the same reason `show` can write
      // to it while a draft lives in a different node entirely.
      "aria-describedby": options.describedBy,
    },
    text,
  });

  const node = el("span", { class: "item-text", children: [button] });

  /**
   * The button's content and name, from `text` and `prefix`: the one place
   * either is written, since `show` and `close` both need it and a bare
   * `textContent` write from either would wipe the prefix's spans.
   *
   * Real text, not a CSS `content`, so the prefix is selectable, copied and
   * read in order. Each node is written only when its words changed, which
   * keeps a selection made in the title through a delta for another field.
   */
  const paintButton = (): void => {
    if (prefix) {
      prefixNode ??= el("span", { class: "item-quantity" });
      words ??= el("span", { class: "item-words" });
      setText(prefixNode, prefix);
      setText(words, text);
      if (button.firstChild !== prefixNode) button.replaceChildren(prefixNode, " ", words);
    } else {
      setText(button, text);
    }
    button.setAttribute("aria-label", options.readOnly?.active() ? options.readOnly.label(text, prefix) : options.label(text, prefix));
  };

  const close = (): void => {
    if (!input || pending) return;
    // Read before the swap: the node about to be removed is the one holding
    // focus. Only when it *had* it — closing is also reachable from a repaint,
    // and stealing focus then would take it off whatever the person moved to.
    //
    // "It" is the editor as a whole, not the field: a click on Save or Cancel
    // moves focus to that button first, and the button leaves with the field.
    // Read the field alone and a Save click would drop focus on `body`.
    const hadFocus = node.contains(document.activeElement);
    input = undefined;
    resized?.disconnect();
    resized = undefined;
    paintButton();
    node.replaceChildren(button);
    if (hadFocus) button.focus();
    options.onToggle?.(false);
    options.group?.closed(self);
  };

  /**
   * Height to fit, so the row neither collapses nor scrolls its own field.
   *
   * `scrollHeight` is 0 in jsdom, which is why nothing about this is asserted
   * in the suite — it is cosmetic, and the browser pass is what checks it.
   */
  const grow = (field: HTMLTextAreaElement): void => {
    // Where the browser fits a field to its content itself (g54), it does so
    // for every cause at once -- typing, a width change, larger text -- and
    // exactly, so nothing here is needed.
    if (sizesItself()) return;
    field.style.height = "auto";
    // Only when a second line is actually needed. `scrollHeight` is a whole
    // number while the text it replaces is not, so setting it for a one-line
    // value moved the row by a fifth of a pixel; leaving the height alone
    // lets one row be exactly one row.
    if (field.scrollHeight > field.clientHeight) field.style.height = `${field.scrollHeight}px`;
  };

  const commitOrKeep = (next: string): boolean => {
    const result = options.commit(next);
    if (typeof result !== "boolean") {
      // Only the list title opts in. Keep its field until the local write is
      // durable; other inline editors retain their synchronous contract.
      const field = input!;
      pending = true;
      field.readOnly = true;
      void result.then((saved) => {
        pending = false;
        field.readOnly = false;
        if (input !== field) return; // Escape or a newer editor owns the UI now.
        if (saved) {
          text = next;
          retry = false;
          close();
        } else {
          // The optimistic store already called show(next). Equality with
          // that value must not turn a retry into an untouched-draft close.
          retry = true;
        }
      });
      return false;
    }
    if (!result) return false;
    text = next;
    return true;
  };

  /**
   * The message beside the field, and the field's own mark. Both exist only
   * while an editor is open; `refuse` fills them and typing clears them.
   */
  let error: HTMLElement | undefined;
  // Set once the item is gone: see `orphan`. The Save it withdraws.
  let orphaned: (() => void) | undefined;
  let saveButton: HTMLButtonElement | undefined;

  const clearError = (): void => {
    if (!input || !error) return;
    error.textContent = "";
    error.hidden = true;
    input.removeAttribute("aria-invalid");
  };

  /**
   * A blank draft, rejected in place (E03): the message appears beside the
   * field, the draft stays, and focus comes back to the field — from wherever
   * the click that asked for the resolution landed, because the person's next
   * keystroke belongs here until the draft is fixed or cancelled.
   */
  const refuse = (): void => {
    if (!input || !error) return;
    error.textContent = EMPTY_ITEM;
    error.hidden = false;
    input.setAttribute("aria-invalid", "true");
    options.announce?.(EMPTY_ITEM);
    input.focus();
  };

  /**
   * Ends an open edit — the one resolution, whichever way it was asked for.
   *
   * Enter, Save, `finish()` from a Details press or another editor opening:
   * all of them come here, so there is exactly one reading of what a draft is
   * worth. An untouched, unmoved draft is dropped rather than written, because
   * only a real change is worth a mutation — and untouched is checked first,
   * since a delta may have moved `text` underneath an editor nobody typed into
   * (see `dirty`). A blank one is refused, unless the caller asked for the old
   * value back instead (`emptyRestores`, which is H01's rule in one call). A
   * refused write keeps the editor open with the draft in it.
   *
   * **Returns true only when the editor is closed** — committed or dropped —
   * and false when it is still open with the draft in it. A caller that was
   * about to open something else has to stop on false, or the refusal costs
   * the person their text.
   *
   * A named function rather than a method on the returned object because
   * `commitOnBlur` calls it from inside `open`, and a method on an object
   * literal that does not exist yet is not something to reach for.
   */
  const finish = (): boolean => {
    if (!input) return true;
    if (pending) return false;
    const next = oneLine(input.value);
    if (!dirty || (next === text && !retry)) {
      close();
      return true;
    }
    if (next === "") {
      if (options.emptyRestores) {
        close();
        return true;
      }
      refuse();
      return false;
    }
    if (!commitOrKeep(next)) {
      // The store refused, so the draft stays — and so does the person's
      // hand. The click that asked for this may have moved focus to another
      // row's button or to Details; left there, the next keystroke lands on
      // that control while the text it was meant for sits in a field nobody
      // is in. Same rule as a blank (`refuse`), for the same reason.
      input.focus();
      return false;
    }
    close();
    return true;
  };

  const open = (): void => {
    if (input) return;
    if (options.readOnly?.active()) return;
    options.group?.opening();
    // Whatever else is open on this screen, first (E04). A refusal leaves that
    // editor open with focus back in it, and this one does not open.
    if (options.group && !options.group.resolve()) return;

    // A textarea rather than an input, and not for multi-line text — Enter
    // still commits. An `<input>` is one line by definition, so opening an
    // editor on a title that wraps to three collapses the row and drags
    // everything below it up the screen: measured at 320px, 174px to 127px.
    // A textarea keeps the shape of the text it replaces.
    editorSerial += 1;
    const errorId = `inline-error-${editorSerial}`;
    const field = el("textarea", {
      class: "item-text-input",
      attrs: {
        "aria-label": options.label(text),
        maxlength: options.maxLength ?? 1000,
        enterkeyhint: "done",
        rows: 1,
        // It is a textarea only so the text keeps its shape; Enter commits
        // rather than inserting a line. Without this a screen reader announces
        // a multi-line textbox and tells the person the opposite.
        "aria-multiline": "false",
        // Points at a real element from the start, empty and hidden until a
        // refusal fills it. On the field, not the button: the button carries
        // its own description (`describedBy`), and the two never meet.
        "aria-describedby": errorId,
      },
    });
    field.value = text;
    input = field;
    dirty = false;
    retry = false;

    // The message, present but silent. `hidden` keeps it out of the tree, and
    // an empty description describes nothing.
    error = el("span", { class: "item-text-error", attrs: { id: errorId, hidden: true } });

    // Save and Cancel, for the edit in hand and no other (E02). Type `button`,
    // because a row can sit inside a form. Not for the heading — `keysOnly`.
    const actions =
      !options.keysOnly &&
      el("span", {
        class: "item-text-actions",
        children: [
          // Named for the item as the row's other controls are ("Actions
          // for Milk"), so a list of buttons read out is not a column of
          // Saves. The visible word comes first, which is what a voice
          // command says. The saved text, not the draft: it names the row.
          (saveButton = el("button", {
            class: "item-text-save",
            text: "Save",
            attrs: { type: "button", "aria-label": `Save ${text}` },
            on: { click: () => void finish() },
          })),
          el("button", {
            class: "item-text-cancel",
            text: "Cancel",
            attrs: { type: "button", "aria-label": `Cancel editing ${text}` },
            on: { click: () => (orphaned ? orphaned() : close()) },
          }),
        ],
      });

    node.replaceChildren(field, error, ...(actions ? [actions] : []));
    grow(field);
    // Where the browser does not fit it (see `grow`): the width changes under
    // an open editor more often than it looks -- the row drops its handle as
    // it starts editing (g54), a phone turns, the window crosses 768 and the
    // handles come or go. The height was fitted to the old width, and with
    // `overflow: hidden` a narrower field clips the words it holds. So it is
    // fitted again on any change of width, and only width: fitting changes
    // the height, which would otherwise call this again.
    if (!sizesItself() && typeof ResizeObserver === "function") {
      let width = field.getBoundingClientRect().width;
      resized = new ResizeObserver(() => {
        const next = field.getBoundingClientRect().width;
        if (next === width) return;
        width = next;
        grow(field);
      });
      resized.observe(field);
    }

    // The design system asks for the current text *selected*, not a caret at
    // one end: replacing a short item is the common edit, and a selection makes
    // that one keystroke instead of a select-all first.
    field.focus();
    field.select();
    options.onToggle?.(true);
    options.group?.opened(self);

    field.addEventListener("input", () => {
      dirty = true;
      clearError();
      grow(field);
    });

    field.addEventListener("keydown", (event: KeyboardEvent) => {
      // Mid-composition, Enter accepts the IME candidate and Escape abandons
      // it. Acting on either here truncates or discards what is being typed in
      // any language that needs an input method to type it at all.
      if (event.isComposing) return;

      if (orphaned) {
        if (event.key === "Enter" || event.key === "Escape") event.preventDefault();
        if (event.key === "Escape") orphaned();
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        // Exactly what Save does, and what a Details press does: one reading
        // of the draft, in `finish`. Rejected in place when blank: no
        // mutation, the editor stays open and focus stays here.
        finish();
      } else if (event.key === "Escape") {
        event.preventDefault();
        close();
      }
    });

    // Deliberately no blur handler for a row.
    //
    // Committing on blur looked right and is wrong: a click on the checkbox
    // blurs the field *first*, so the draft would be saved and the editor
    // closed by the very gesture the design system says must "change only
    // completion and preserve the draft" — and a background click would save,
    // which it says must not. So focus leaving does nothing at all: the editor
    // stays open with the draft in it.
    //
    // What does end it, besides Enter, Escape, Save and Cancel: an *explicit*
    // editor switch. Opening another row's editor, the heading's rename or
    // the Details panel resolves this draft first, through `finish` — a valid
    // one is committed, an untouched one dropped, a blank one refused and the
    // switch stopped (E04). That is not a blur rule and must not become one:
    // the distinction is between a gesture that names another editor and a
    // gesture that merely takes focus, and the checkbox is the second kind.
    //
    // All of which is about a *row*, and `commitOnBlur` is why that had to be
    // said out loud. Every reason above names the checkbox two pixels away:
    // the gesture that blurs the field is the gesture the design system says
    // must change only completion and keep the draft. A list's heading has no
    // such neighbour — nothing sits beside the title that a person could aim
    // at and lose their typing to — so there blur is the only cost left, and
    // H01 spends it deliberately by keeping the blur-save the editable title
    // had before this helper existed.
    if (options.commitOnBlur) {
      // `finish` and not a copy of it: an untouched, empty or unmoved draft is
      // dropped rather than written, which is exactly H01's empty rule again,
      // and a refused write leaves the editor open with the text still in it.
      //
      // Safe against its own close: `close()` replaces the focused field,
      // which fires a blur, and `finish` returns immediately once `input` is
      // undefined. And `close()` only restores focus when the editor still
      // held it, which on this path it does not — so nothing is stolen from
      // wherever the person actually went.
      field.addEventListener("blur", event => {
        if (!navigationHoldsBlur(event.relatedTarget)) finish();
      });
    }
  };

  button.addEventListener("click", () => {
    if (options.readOnly?.active()) options.readOnly.activate();
    else open();
  });

  const self: InlineText = {
    node,
    editing: () => input !== undefined,
    pending: () => pending,
    dirty: () => input !== undefined && dirty,
    show(next: string, nextPrefix?: string) {
      // No special case for an open editor, and it needs none twice over: the
      // draft lives in the input, which is a different node from the button
      // this writes to, and closing repaints the row from the store anyway.
      // An earlier version returned early here to "protect the draft";
      // measured, it changed no behaviour at all, so it was a guard that
      // looked load-bearing and was not. What actually carries this is
      // `onToggle`.
      text = next;
      prefix = nextPrefix || undefined;
      // Only what changed: a repaint for another field would otherwise
      // clear a selection made in the title (g54).
      paintButton();
    },
    open,
    finish,
    orphan(message, onDiscard) {
      if (!input || !error) return;
      orphaned = onDiscard;
      input.readOnly = true;
      input.removeAttribute("aria-invalid");
      saveButton?.remove();
      error.textContent = message;
      error.hidden = false;
      error.classList.add("item-text-gone");
      options.announce?.(message);
      options.group?.closed(self);
    },
  };
  return self;
}

/**
 * What an item row looks like, wherever it is drawn.
 *
 * Shared between a list and a projection because the overdue treatment has to
 * be: it used to be a colour and nothing else, which is a WCAG 1.4.1 failure,
 * and the fix is only worth having in one place. The word travels in the meta
 * line rather than as a badge so a row keeps its three-node shape.
 *
 * Deliberately *not* a shared row: a list's row carries a drag handle, keyboard
 * moves and an ends lookup against its own siblings, and its panel offers
 * Insert below; a projection's row carries none of them, because an anchor in
 * a cross-list view names no unambiguous place. Only the presentation is
 * common.
 *
 * `kind` is the kind of the list the item is in *now*, read from the store by
 * the caller, and required so no caller can forget it. A Shopping row's
 * quantity leads its text as `prefix` instead of opening the meta line (g58):
 * "200g Crème fraîche", not "Crème fraîche" over "200g". Every other kind
 * keeps the quantity in the meta line exactly as before, and so does a row
 * whose list is not known (`undefined`), such as one whose list is being
 * deleted.
 */
export function rowPresentation(
  item: Item,
  options: {
    kind: ListKind | undefined;
    today?: string;
    selected?: boolean;
    editing?: boolean;
    /** Who added the row, when the row says so (g72): see `addedBy`. */
    addedBy?: string;
  },
): { className: string; meta: string; metaParts: MetaPart[]; overdue: boolean; prefix: string | undefined } {
  const today = options.today ?? todayIso();
  const overdue = Boolean(item.due && !item.done && isOverdue(item.due, today));

  // The date said it and the colour said it; neither says it to somebody who
  // cannot tell the two reds apart from the ordinary grey. So the word stays,
  // and it travels with the date it qualifies (g56): the danger colour is
  // scoped to that one fragment, and a quantity or a repeat beside it stays
  // as neutral as it is on any other row.
  const parts: MetaPart[] = [];
  const shopping = options.kind === "shopping";
  if (!shopping && item.quantity) parts.push({ text: item.quantity });
  // The item's own words, quiet and before the date (g79): never a badge or a heading.
  if (item.category) parts.push({ text: item.category, category: true });
  if (item.due) {
    const when = formatDue(item.due, today);
    parts.push(overdue ? { text: `${when} · Overdue`, overdue: true } : { text: when });
  }
  if (item.repeat) parts.push({ text: `Repeats ${item.repeat}` });
  if (item.repeat && item.lastDoneOn === today) parts.push({ text: "Done today" });
  // Last and quietest: who added it is about the row, not about the item.
  if (options.addedBy !== undefined) parts.push({ text: `Added by ${options.addedBy}`, creator: options.addedBy });

  return {
    // Assembled whole rather than toggled: the caller assigns `className`, so
    // anything left out here is quietly dropped on the next repaint.
    className: [
      "item",
      item.done && "done",
      overdue && "overdue",
      options.selected && "selected",
      // Here rather than toggled by whoever opens the editor, for the reason
      // the comment above gives: the caller assigns `className` whole, so a
      // class added anywhere else survives exactly until the next delta.
      options.editing && "editing",
    ]
      .filter(Boolean)
      .join(" "),
    meta: parts.map((part) => part.text).join(" · "),
    metaParts: parts,
    overdue,
    // A blank or a bare zero leads nothing, and is left as it is stored.
    prefix: shopping ? displayQuantity(item.quantity) : undefined,
  };
}

/**
 * The note viewer's line of facts (g77), from the same reading a row's meta
 * line comes from: a Shopping quantity first, then the quantity where
 * another kind holds one, the category (g79), the due date, the repeat and
 * "Done today", and "Done" for an item that is.
 */
export function noteFacts(item: Item, kind: ListKind, today = todayIso()): MetaPart[] {
  const { prefix, metaParts } = rowPresentation(item, { kind, today });
  const parts = prefix ? [{ text: prefix }, ...metaParts] : [...metaParts];
  if (item.done && !metaParts.some((part) => part.text === "Done today")) parts.push({ text: "Done" });
  return parts;
}

/**
 * A row's note preview opens the note page on a tap or a click (g139), the
 * menu's View note by a quicker way. The whole two-line block is the target,
 * and the item's text still starts an edit.
 *
 * Pointer only: the preview stays a span and is no Tab stop, because View
 * note in the row's menu already reaches the page from a keyboard or a
 * screen reader, and a link would add a stop to every row with a note. A
 * click that ends a selection reaching into the preview, from inside it or
 * extended into it with Shift, is someone copying the words, not asking for
 * the page.
 */
export function noteOpener(note: HTMLElement, open: () => void): void {
  note.dataset["opens"] = "";
  // Focusable by the press but still no Tab stop. A press on a span that
  // cannot hold the focus leaves it on the body, and the shell then sees no
  // focus on the outgoing screen and gives the note page's heading none.
  note.tabIndex = -1;
  note.addEventListener("click", () => {
    const selection = document.getSelection();
    if (selection && !selection.isCollapsed && selection.rangeCount > 0 && selection.getRangeAt(0).intersectsNode(note)) return;
    open();
  });
}

/** One fragment of a row's meta line; only an overdue date is flagged. */
export interface MetaPart {
  text: string;
  overdue?: boolean;
  /** The name in an "Added by" part (g72), isolated so a right-to-left name cannot reorder the line. */
  creator?: string;
  /** The item's category (g79), in its own span so a long one ends in an ellipsis instead of wrapping the row. */
  category?: true;
}

/**
 * The name "Added by" says (g72): the creator's own current name, or
 * "someone" when this device does not know it, they never gave one, or their
 * identity is gone. Never an email and never an id.
 */
export function creatorName(store: Pick<Store, "creator">, creatorId: string | null | undefined): string {
  const name = creatorId ? store.creator(creatorId)?.name : undefined;
  return name ? name : "someone";
}

/**
 * Whether a row made by `creatorId` is the viewer's own (g72). A row with no
 * creator yet was made on this device and has not come back from the server,
 * so it is the viewer's; `null` is a row the server never credited.
 */
export function ownRow(creatorId: string | null | undefined, viewer: string | null | undefined): boolean {
  return creatorId === undefined || (viewer != null && creatorId === viewer);
}

/**
 * The label a row carries on a shared list (g72): only for a row someone
 * else added. The viewer's own rows stay quiet; Details says "Added by you".
 */
export function addedBy(store: Pick<Store, "creator">, creatorId: string | null | undefined, viewer: string | null | undefined): string | undefined {
  return ownRow(creatorId, viewer) ? undefined : creatorName(store, creatorId);
}

const ADDED_BY = "Added by ";

/** What Details says about who added the item (g72), the viewer's own included. */
export function attributionLine(store: Pick<Store, "creator">, creatorId: string | null | undefined, viewer: string | null | undefined): string {
  return ownRow(creatorId, viewer) ? `${ADDED_BY}you` : `${ADDED_BY}${creatorName(store, creatorId)}`;
}

/**
 * Writes `attributionLine`'s words into `node`, the name isolated as a row's
 * meta line isolates it: a right-to-left name would otherwise take "Added by"
 * into its own direction and read after it.
 */
export function paintAttribution(node: HTMLElement, line: string | undefined): void {
  node.hidden = line === undefined;
  if (line === undefined) node.replaceChildren();
  else if (line.startsWith(ADDED_BY)) node.replaceChildren(ADDED_BY, el("bdi", { text: line.slice(ADDED_BY.length) }));
  else node.replaceChildren(line);
}

const paintedMeta = new WeakMap<Element, string>();

/**
 * Draws a row's meta line from `rowPresentation`'s parts, with the overdue
 * date in its own span so only that fragment takes the danger colour.
 *
 * Rebuilt only when the parts change, for the reason `setText` gives: a
 * repaint that replaces identical text nodes clears a selection someone had
 * made in them, and a delta for another field of the row repaints this line.
 */
export function paintMeta(node: HTMLElement, parts: MetaPart[]): void {
  const key = JSON.stringify(parts);
  if (paintedMeta.get(node) === key) return;
  paintedMeta.set(node, key);
  const children: (Node | string)[] = [];
  parts.forEach((part, index) => {
    if (index > 0) children.push(" · ");
    children.push(
      part.overdue ? el("span", { class: "item-meta-overdue", text: part.text })
        : part.category ? el("span", { class: "item-meta-category", text: part.text })
        : part.creator !== undefined
          ? el("span", { class: "item-meta-creator", children: [ADDED_BY, el("bdi", { text: part.creator })] })
          : part.text,
    );
  });
  node.replaceChildren(...children);
  node.hidden = parts.length === 0;
}

/**
 * What a list's kind *offers*, never what it can hold.
 *
 * A quantity on a to-do list and a due date on a shopping list are both
 * perfectly legal, and an agent sets them freely — so a field appears when the
 * kind suggests it **or** when the item already has a value. Hiding a field
 * that holds something is how data goes quietly missing.
 */
// `other` makes no claim about what belongs in it, so it offers both. Only the
// two opinionated kinds leave a field out.
export function offersQuantity(kind: ListKind, item?: Item): boolean {
  return kind !== "todo" || Boolean(item?.quantity);
}

export function offersDue(kind: ListKind, item?: Item): boolean {
  return kind !== "shopping" || Boolean(item?.due);
}

/**
 * A list's kind changes how it is presented, not what it can hold — so these
 * are labels, and nothing outside the views branches on them.
 */
export const KIND_LABEL: Record<ListKind, string> = {
  todo: "To-do",
  shopping: "Shopping",
  other: "Other",
};

/**
 * One glyph per kind, leading every row of the index and every entry of the
 * rail (L03, S02). Restraint is by colour, not size: the sweep gates icons to
 * 18–20px, and a smaller one would read as a bullet.
 */
export const KIND_ICON: Record<ListKind, IconName> = {
  todo: "checklist",
  shopping: "cart",
  other: "list",
};

/**
 * The accessible name of a list's row or rail entry: "Weekend, Shopping,
 * 8 unchecked items". The kind is said only when it is not the default, as
 * it is only shown then; the count is part of the name so the badge beside it
 * is never a stop of its own.
 */
export function listLabel(list: Pick<List, "name" | "kind">, count: number): string {
  const kind = list.kind === "todo" ? [] : [KIND_LABEL[list.kind]];
  return [list.name, ...kind, uncheckedPhrase(count)].join(", ");
}

/** A count badge, and the one way its number is set. */
export function countBadge(): HTMLSpanElement {
  // Decorative: the number is already in the link's name, and a span with
  // text of its own would have a screen reader say it twice.
  return el("span", { class: "count", attrs: { "aria-hidden": "true" } });
}

/**
 * Sets the number on a badge in place. Zero is shown and muted rather than
 * left blank — an entry with no number reads as "not counted", which is a
 * different claim from "nothing left". No count is ever unknown while a list
 * is on screen: the mirror hydrates lists and items together, so a placeholder
 * state would be a state that cannot occur.
 */
export function setCount(badge: HTMLElement, count: number): void {
  const text = String(count);
  if (badge.textContent !== text) badge.textContent = text;
  badge.classList.toggle("count-zero", count === 0);
}

export interface ChoiceControl<T extends string> {
  node: HTMLFieldSetElement;
  value(): T;
  /** Sets the choice from outside — a delta from another device. */
  set(next: T): void;
}

export type KindControl = ChoiceControl<ListKind>;
let choiceSerial = 0;

/**
 * A row of choices, shared by list kind and export format.
 *
 * Native radios inside their labels, styled as one segmented control, so
 * arrow keys, type-to-select and a screen reader's "2 of 3" come from the
 * browser and not from here. Each control gets its own `name`, because the
 * New list popup and List options can be in the document at once and two
 * groups sharing a name would be one group.
 *
 * The radio is a real, transparent input filling its label rather than a
 * visually hidden one: the focus ring is drawn on the focused node, and the
 * sweep reads it from the focused node, so a ring painted on the label
 * through `:has()` would be a ring the check could not see.
 */
/** `detail`, when given, follows the label inside the option: the share dialog's On and Off (g73). */
export function choiceControl<T extends string>(legend: string, choices: readonly { value: T; label: string; detail?: Node }[], value: T): ChoiceControl<T> {
  choiceSerial += 1;
  const name = `choice-${choiceSerial}`;
  const radios = choices.map((choice) =>
    el("input", { attrs: { type: "radio", name, value: choice.value, checked: choice.value === value } }),
  );
  const node = el("fieldset", {
    class: "kind-control",
    children: [
      el("legend", { class: "field-label", text: legend }),
      el("div", {
        class: "kind-options",
        children: radios.map((radio, at) =>
          el("label", {
            class: "kind-option",
            children: [radio, el("span", { text: choices[at]!.label }), ...(choices[at]!.detail ? [choices[at]!.detail!] : [])],
          }),
        ),
      }),
    ],
  });
  return {
    node,
    value: () => (radios.find((radio) => radio.checked)?.value as T | undefined) ?? value,
    set(next) {
      for (const radio of radios) radio.checked = radio.value === next;
    },
  };
}

export function kindControl(value: ListKind): KindControl {
  return choiceControl("List kind", LIST_KINDS.map(kind => ({ value: kind, label: KIND_LABEL[kind] })), value);
}
